import React from 'react';
import { motion } from 'framer-motion';
// Updated imports to match the contact section icons
import { FaMapMarkerAlt, FaEnvelope, FaGlobe, FaHandshake, FaUsers, FaShieldAlt } from 'react-icons/fa';
import { DollarSign } from 'lucide-react'; // Import for Amama Bypass icon

// Define the Callout Data as it appears in FounderCallouts
const calloutsData = [
    {
        title: "Hands-on company building",
        description: "Early customer loops, design sprints, and enterprise sales enablement.",
        icon: 'Handshake',
        color: 'bg-indigo-500',
    },
    {
        title: "Talent and hiring support",
        description: "Across engineering, product, and GTM in EU–GCC–India corridors.",
        icon: 'Users',
        color: 'bg-fuchsia-500',
    },
    {
        title: "Regulatory navigation",
        description: "For data, healthcare, and fintech markets.",
        icon: 'ShieldCheck',
        color: 'bg-teal-500',
    },
    {
        title: "Access Amama Bypass",
        description: "Non-dilutive financing options (like RBF/bridge) to maximize equity retention during key scaling milestones.",
        icon: 'DollarSign',
        color: 'bg-cyan-500',
    },
];

// Map icon names to imported components (expanded map from FounderCallouts)
const iconMap = {
    MapMarker: FaMapMarkerAlt,
    Envelope: FaEnvelope,
    Globe: FaGlobe,
    Handshake: FaHandshake,
    Users: FaUsers,
    ShieldCheck: FaShieldAlt,
    DollarSign: DollarSign,
};


const Contact = () => {
    return (
        <motion.section
            className="bg-background py-12 md:py-16" // Use padding like FounderCallouts
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="container mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">

                    {/* Left Column: Text and Thematic Callouts (Identical to FounderCallouts) */}
                    <div>
                        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                            Building With You
                        </h1>
                        <p className="text-lg text-muted-foreground max-w-lg mb-12">
                            We deploy capital and our network to accelerate your path to market leadership, with hands-on support in the key areas below.
                        </p>

                        {/* Thematic Callouts Grid (Identical to FounderCallouts) */}
                        <div className="space-y-8">
                            {calloutsData.map((callout, index) => {
                                const IconComponent = iconMap[callout.icon];
                                return (
                                    <motion.div
                                        key={index}
                                        className="flex items-start gap-4 p-4 border border-border rounded-xl transition-all duration-300 hover:border-blue-500/50 hover:shadow-md"
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        viewport={{ once: true }}
                                    >
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white ${callout.color} flex-shrink-0 mt-1`}>
                                            {IconComponent && <IconComponent className="text-xl" />}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-lg text-foreground">{callout.title}</h3>
                                            <p className="text-muted-foreground text-base mt-1">{callout.description}</p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Right Column: Contact Form (Identical to FounderCallouts) */}
                    <motion.div
                        className="bg-card p-8 md:p-10 rounded-2xl shadow-lg border border-border"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl font-bold text-foreground mb-6">Let's Connect</h2> {/* Title synced */}
                        <form className="space-y-6">
                            <div>
                                <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-2">
                                    Full Name
                                </label>
                                <input
                                    type="text"
                                    id="fullName"
                                    placeholder="John Doe"
                                    className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                />
                            </div>
                            <div>
                                <label htmlFor="emailAddress" className="block text-sm font-medium text-foreground mb-2">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    id="emailAddress"
                                    placeholder="john@company.com"
                                    className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                />
                            </div>
                            <div>
                                <label htmlFor="company" className="block text-sm font-medium text-foreground mb-2">
                                    Company
                                </label>
                                <input
                                    type="text"
                                    id="company"
                                    placeholder="Your Company"
                                    className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                                />
                            </div>
                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                                    Message
                                </label>
                                <textarea
                                    id="message"
                                    rows="5"
                                    placeholder="Tell us about your vision..."
                                    className="w-full px-4 py-3 bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground resize-y"
                                ></textarea>
                            </div>
                            <button
                                type="submit"
                                className="w-full py-3 px-6 rounded-lg text-white font-semibold 
                                         bg-gradient-to-r from-blue-500 to-cyan-500 
                                         hover:from-blue-600 hover:to-cyan-600 
                                         transition-all duration-300 shadow-lg"
                            >
                                Send Message
                            </button>
                        </form>
                    </motion.div>
                </div>
            </div>
        </motion.section>
    );
};

export default Contact;